/**
 * Story 049 — Dashboard widget canvas.
 *
 * Mirrors `AnalysisWorkspace`'s RGL wiring verbatim (drag handle DOM,
 * role, tabIndex, aria-label, header chrome typography + border,
 * body `overflow: auto`, `compactType`, breakpoints, COLS, rowHeight,
 * margin, draggable handle class). UX brief §6 — "Drag / resize / hide
 * gesture vocabulary (mirror AnalysisWorkspace verbatim)".
 *
 * Deltas vs AnalysisWorkspace:
 *   - Persistence is server-side via `useDashboardLayoutMutation`
 *     (debounced 600ms). No localStorage; the user's headline
 *     requirement is cross-device.
 *   - Drag handle also responds to `+` / `-` for resize (UX brief
 *     §6.2). AnalysisWorkspace today only nudges; we extend the
 *     keyboard contract here. When `B-049.1` lifts the shared
 *     primitive, both surfaces will inherit this contract.
 */
import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactElement, ReactNode } from "react";
import { Box, Skeleton } from "@mui/material";
import {
  Responsive,
  WidthProvider,
  type Layout,
  type Layouts,
} from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import {
  ALL_WIDGETS,
  BREAKPOINTS,
  COLS,
  DEFAULT_LAYOUTS,
  DRAG_HANDLE_CLASS,
  MARGIN,
  ROW_HEIGHT,
  WIDGET_TITLE,
  factoryEnvelope,
  type DashboardLayoutEnvelope,
  type WidgetKey,
} from "./shared/factoryDefaults.js";
import { WidgetSlot } from "./widgets/WidgetSlot.js";
import { KpiOpenInspections } from "./widgets/KpiOpenInspections.js";
import { KpiDefectsThisMonth } from "./widgets/KpiDefectsThisMonth.js";
import { LatestInspections } from "./widgets/LatestInspections.js";
import { RecentActivity } from "./widgets/RecentActivity.js";

// MAP-4 — code-split the Fleet Map (MapLibre + Supercluster + pmtiles are
// heavy; non-map dashboards must not pay for them on first paint).
const FleetMap = lazy(() => import("./widgets/FleetMap/FleetMap.js"));

// ONGOING-2 — code-split the Ongoing Projects card (self-contained two-pane
// widget; only the dashboards that show it pay for its body).
const OngoingProjects = lazy(
  () => import("./widgets/OngoingProjects/OngoingProjects.js"),
);

const ResponsiveGridLayout = WidthProvider(Responsive);

const WIDGET_RENDER: Record<WidgetKey, () => ReactNode> = {
  "kpi-open-inspections": () => <KpiOpenInspections />,
  "kpi-defects-this-month": () => <KpiDefectsThisMonth />,
  "latest-inspections": () => <LatestInspections />,
  "recent-activity": () => <RecentActivity />,
  "fleet-map": () => (
    <Suspense
      fallback={
        <Box sx={{ p: 1, height: "100%" }}>
          <Skeleton variant="rounded" width="100%" height="100%" />
        </Box>
      }
    >
      <FleetMap />
    </Suspense>
  ),
  "ongoing-projects": () => (
    <Suspense
      fallback={
        <Box sx={{ p: 1, height: "100%" }}>
          <Skeleton variant="rounded" width="100%" height="100%" />
        </Box>
      }
    >
      <OngoingProjects />
    </Suspense>
  ),
};

export interface DashboardWorkspaceProps {
  /**
   * Server-of-record envelope. `null` means "user has never
   * customized" — apply factory defaults. Updated by the parent when
   * the GET resolves so a stale render reflows once the saved layout
   * arrives.
   */
  initialEnvelope: DashboardLayoutEnvelope | null;
  /**
   * Persistence callback. The workspace funnels every gesture through
   * here; the implementation debounces and PUTs.
   */
  onPersist: (envelope: DashboardLayoutEnvelope) => void;
  /**
   * Imperative reset bridge. The page-level Reset button (in the
   * action bar) and the gear-menu Reset item call this — the
   * workspace exposes its handler via an imperative ref instead of a
   * prop so the action bar (which lives above the workspace in the
   * DOM tree) can drive the in-grid state without re-derivation.
   */
  resetSignal?: { ref: React.RefObject<(() => void) | null> };
  /**
   * Bridge for restoring a hidden widget from the gear menu. Same
   * imperative-ref pattern as `resetSignal`.
   */
  restoreSignal?: {
    ref: React.RefObject<((key: WidgetKey) => void) | null>;
  };
  /**
   * Read-only mirror of the hidden state — the gear menu reads from
   * this to decide which widgets to list as "restore" candidates.
   */
  hiddenRef?: React.RefObject<Record<string, boolean>>;
}

interface WorkspaceState {
  layouts: Layouts;
  hidden: Record<string, boolean>;
}

function envelopeToState(env: DashboardLayoutEnvelope | null): WorkspaceState {
  if (!env) {
    return {
      layouts: {
        lg: [...(DEFAULT_LAYOUTS.lg ?? [])],
        md: [...(DEFAULT_LAYOUTS.md ?? [])],
        sm: [...(DEFAULT_LAYOUTS.sm ?? [])],
        xs: [...(DEFAULT_LAYOUTS.xs ?? [])],
      },
      hidden: {},
    };
  }
  return {
    layouts: {
      lg: [...env.layouts.lg],
      md: [...env.layouts.md],
      sm: [...env.layouts.sm],
      xs: [...env.layouts.xs],
    },
    hidden: { ...env.hidden },
  };
}

function stateToEnvelope(state: WorkspaceState): DashboardLayoutEnvelope {
  return {
    version: 1,
    layouts: {
      lg: state.layouts.lg ?? [],
      md: state.layouts.md ?? [],
      sm: state.layouts.sm ?? [],
      xs: state.layouts.xs ?? [],
    },
    hidden: { ...state.hidden },
  };
}

/**
 * Stable, RGL-private-field-free serialisation of an envelope, used for
 * change detection. react-grid-layout mutates layout entries in place during
 * render (injecting `moved` / `static` / `isDraggable` / `isResizable` /
 * `placeholder`), so a raw `JSON.stringify` of the wire never matches the
 * clean envelope we seed from the server — every `onLayoutChange` then looks
 * like a real change and triggers a persist. Once a newly-registered widget
 * (e.g. `fleet-map`) forces a back-fill save on mount, that turns into an
 * endless persist → onSuccess(setQueryData) → resync → re-render →
 * onLayoutChange → persist oscillation, and the dashboard visibly "flickers".
 * Comparing only the contract fields, in a stable order, makes detection
 * idempotent so the back-fill persists exactly once and then settles.
 */
export function canonicalEnvelope(env: DashboardLayoutEnvelope): string {
  const entry = (l: Layout): Record<string, number | string> => ({
    i: l.i,
    x: l.x,
    y: l.y,
    w: l.w,
    h: l.h,
    ...(typeof l.minW === "number" ? { minW: l.minW } : {}),
    ...(typeof l.minH === "number" ? { minH: l.minH } : {}),
  });
  const bp = (arr: Layout[]): Record<string, number | string>[] =>
    arr.map(entry).sort((a, b) => String(a.i).localeCompare(String(b.i)));
  const hidden = Object.keys(env.hidden)
    .filter((k) => env.hidden[k])
    .sort();
  return JSON.stringify({
    version: env.version,
    layouts: {
      lg: bp(env.layouts.lg),
      md: bp(env.layouts.md),
      sm: bp(env.layouts.sm),
      xs: bp(env.layouts.xs),
    },
    hidden,
  });
}

export function DashboardWorkspace({
  initialEnvelope,
  onPersist,
  resetSignal,
  restoreSignal,
  hiddenRef,
}: DashboardWorkspaceProps): ReactElement {
  const [state, setState] = useState<WorkspaceState>(() =>
    envelopeToState(initialEnvelope),
  );
  const [breakpoint, setBreakpoint] = useState<string>("lg");

  // Re-sync local state whenever the parent hands us a freshly-fetched
  // envelope. The parent is the source of truth for "what was last
  // persisted to the server"; the workspace is the source of truth for
  // "what the user is currently moving around". Identity check on the
  // envelope reference is sufficient — TanStack Query never mutates in
  // place. Without this resync, AC3's stub-then-render flow renders the
  // factory defaults instead of the seeded custom layout because the
  // GET resolves AFTER the workspace mounts.
  const lastEnvelopeRef = useRef<DashboardLayoutEnvelope | null>(
    initialEnvelope,
  );
  useEffect(() => {
    if (initialEnvelope === lastEnvelopeRef.current) return;
    lastEnvelopeRef.current = initialEnvelope;
    const nextState = envelopeToState(initialEnvelope);
    // The parent is now the source of truth — mirror that shape into
    // `lastPersistedRef` so the next `onLayoutChange` doesn't think
    // RGL's first-paint re-emit is a divergence from the server.
    lastPersistedRef.current = canonicalEnvelope(stateToEnvelope(nextState));
    setState(nextState);
  }, [initialEnvelope]);

  // Mirror hidden into the optional ref so the page-level gear menu
  // (which lives above DashboardWorkspace in the DOM tree) can read
  // the current set without prop-drilling state down and back up.
  useEffect(() => {
    if (hiddenRef) {
      hiddenRef.current = state.hidden;
    }
  }, [state.hidden, hiddenRef]);

  // RGL fires `onLayoutChange` on every mount + every drag-end /
  // resize-end + during breakpoint reflow. Persist only when the wire
  // envelope actually changes vs the last shape we sent (or were
  // handed) — that filters out the mount-time fire AND the
  // breakpoint-sync fires that recompute layouts without user intent.
  //
  // The "last known" reference is seeded from the parent's initial
  // envelope and updated every time we persist. JSON.stringify is a
  // pragmatic stand-in for a deep-equal here — the envelope is a
  // small bounded object (≤ 16 KB per BR-019), and stringification
  // dominates the work TanStack Query was going to do on the same
  // payload during serialization anyway.
  const lastPersistedRef = useRef<string>(
    canonicalEnvelope(stateToEnvelope(envelopeToState(initialEnvelope))),
  );
  const handleLayoutChange = useCallback(
    (_current: Layout[], all: Layouts) => {
      setState((prev) => {
        const next: WorkspaceState = {
          layouts: {
            lg: all.lg ?? prev.layouts.lg ?? [],
            md: all.md ?? prev.layouts.md ?? [],
            sm: all.sm ?? prev.layouts.sm ?? [],
            xs: all.xs ?? prev.layouts.xs ?? [],
          },
          hidden: prev.hidden,
        };
        const wire = stateToEnvelope(next);
        const serialized = canonicalEnvelope(wire);
        if (serialized !== lastPersistedRef.current) {
          lastPersistedRef.current = serialized;
          onPersist(wire);
        }
        return next;
      });
    },
    [onPersist],
  );

  const handleHide = useCallback(
    (key: WidgetKey) => {
      setState((prev) => {
        const next: WorkspaceState = {
          layouts: prev.layouts,
          hidden: { ...prev.hidden, [key]: true },
        };
        // When a widget is hidden we also strip it from `layouts.lg`
        // (and the other breakpoints) — AC7's PUT-body assertion reads
        // "after Restore the widget is back in `layouts.lg`", meaning
        // hidden widgets are NOT in `layouts.lg`. Drop the entry from
        // every breakpoint so the wire format stays canonical.
        const stripKey = (arr: Layout[] | undefined): Layout[] =>
          (arr ?? []).filter((l) => l.i !== key);
        next.layouts = {
          lg: stripKey(prev.layouts.lg),
          md: stripKey(prev.layouts.md),
          sm: stripKey(prev.layouts.sm),
          xs: stripKey(prev.layouts.xs),
        };
        const wire = stateToEnvelope(next);
        lastPersistedRef.current = canonicalEnvelope(wire);
        onPersist(wire);
        return next;
      });
    },
    [onPersist],
  );

  const handleRestore = useCallback(
    (key: WidgetKey) => {
      setState((prev) => {
        // Remove the hidden flag.
        const nextHidden: Record<string, boolean> = {};
        for (const [k, v] of Object.entries(prev.hidden)) {
          if (k === key) continue;
          if (v) nextHidden[k] = v;
        }
        // Re-insert the widget at its factory-default position for
        // each breakpoint where it's missing. RGL's vertical compaction
        // will pack it below the existing stack.
        const ensureKey = (
          arr: Layout[] | undefined,
          bp: keyof typeof DEFAULT_LAYOUTS,
        ): Layout[] => {
          const current = arr ?? [];
          if (current.some((l) => l.i === key)) return current;
          const fallback = (DEFAULT_LAYOUTS[bp] ?? []).find((l) => l.i === key);
          if (!fallback) return current;
          return [...current, fallback];
        };
        const next: WorkspaceState = {
          layouts: {
            lg: ensureKey(prev.layouts.lg, "lg"),
            md: ensureKey(prev.layouts.md, "md"),
            sm: ensureKey(prev.layouts.sm, "sm"),
            xs: ensureKey(prev.layouts.xs, "xs"),
          },
          hidden: nextHidden,
        };
        const wire = stateToEnvelope(next);
        lastPersistedRef.current = canonicalEnvelope(wire);
        onPersist(wire);
        return next;
      });
    },
    [onPersist],
  );

  const handleReset = useCallback(() => {
    const fresh = factoryEnvelope();
    setState(envelopeToState(fresh));
    lastPersistedRef.current = canonicalEnvelope(fresh);
    onPersist(fresh);
  }, [onPersist]);

  // Wire the imperative bridges so the action bar / gear menu can call
  // into the workspace without prop-drilling state.
  useEffect(() => {
    if (resetSignal) {
      resetSignal.ref.current = handleReset;
    }
    return () => {
      if (resetSignal) {
        resetSignal.ref.current = null;
      }
    };
  }, [resetSignal, handleReset]);

  useEffect(() => {
    if (restoreSignal) {
      restoreSignal.ref.current = handleRestore;
    }
    return () => {
      if (restoreSignal) {
        restoreSignal.ref.current = null;
      }
    };
  }, [restoreSignal, handleRestore]);

  const handleKeyboardNudge = useCallback(
    (key: WidgetKey, dx: number, dy: number, dw: number, dh: number) => {
      setState((prev) => {
        const bp =
          breakpoint in prev.layouts ? (breakpoint as keyof Layouts) : "lg";
        const arr =
          prev.layouts[bp] ?? DEFAULT_LAYOUTS[bp] ?? DEFAULT_LAYOUTS.lg ?? [];
        const cols = COLS[bp as keyof typeof COLS];
        const updated = arr.map((entry) => {
          if (entry.i !== key) return entry;
          const minW = entry.minW ?? 1;
          const minH = entry.minH ?? 1;
          const nextW = Math.max(minW, Math.min(cols, entry.w + dw));
          const nextH = Math.max(minH, entry.h + dh);
          const nextX = Math.max(0, Math.min(cols - nextW, entry.x + dx));
          const nextY = Math.max(0, entry.y + dy);
          return { ...entry, x: nextX, y: nextY, w: nextW, h: nextH };
        });
        const next: WorkspaceState = {
          layouts: { ...prev.layouts, [bp]: updated },
          hidden: prev.hidden,
        };
        const wire = stateToEnvelope(next);
        lastPersistedRef.current = canonicalEnvelope(wire);
        onPersist(wire);
        return next;
      });
    },
    [breakpoint, onPersist],
  );

  const visibleKeys = useMemo(
    () => ALL_WIDGETS.filter((k) => state.hidden[k] !== true),
    [state.hidden],
  );

  // Filter the layouts the grid sees to visible widgets only — RGL
  // emits a warning on unmatched layout entries, and hidden widgets
  // shouldn't have layout entries (per AC7's body shape).
  const filteredLayouts = useMemo<Layouts>(() => {
    const out: Layouts = { lg: [], md: [], sm: [], xs: [] };
    for (const bp of ["lg", "md", "sm", "xs"] as const) {
      const stored = (state.layouts[bp] ?? []).filter((l) =>
        visibleKeys.includes(l.i as WidgetKey),
      );
      // Back-fill any visible widget that's missing from the stored
      // layout (legacy or seeded envelope without all four widgets).
      // Place it at the bottom of the existing stack to avoid colliding
      // with the seed. Same pattern as AnalysisWorkspace.
      const have = new Set(stored.map((l) => l.i));
      let nextY = stored.reduce((acc, l) => Math.max(acc, l.y + l.h), 0);
      const missing: Layout[] = [];
      for (const k of ALL_WIDGETS) {
        if (!visibleKeys.includes(k)) continue;
        if (have.has(k)) continue;
        const fallback = (DEFAULT_LAYOUTS[bp] ?? []).find((l) => l.i === k);
        if (!fallback) continue;
        missing.push({ ...fallback, x: 0, y: nextY });
        nextY += fallback.h;
      }
      const merged = [...stored, ...missing];
      out[bp] = merged.length > 0 ? merged : (DEFAULT_LAYOUTS[bp] ?? []);
    }
    return out;
  }, [state.layouts, visibleKeys]);

  const currentBpLayout = useMemo(() => {
    const bp =
      breakpoint in state.layouts ? (breakpoint as keyof Layouts) : "lg";
    return filteredLayouts[bp] ?? filteredLayouts.lg ?? [];
  }, [breakpoint, state.layouts, filteredLayouts]);

  return (
    <Box sx={{ width: "100%", height: "100%", minHeight: 0 }}>
      <ResponsiveGridLayout
        className="aero-dashboard-workspace"
        layouts={filteredLayouts}
        breakpoints={BREAKPOINTS}
        cols={COLS}
        rowHeight={ROW_HEIGHT}
        margin={MARGIN}
        draggableHandle={`.${DRAG_HANDLE_CLASS}`}
        onLayoutChange={handleLayoutChange}
        onBreakpointChange={(bp) => {
          setBreakpoint(bp);
        }}
        compactType="vertical"
      >
        {visibleKeys.map((key) => {
          const layoutEntry = currentBpLayout.find((l) => l.i === key);
          const renderBody = WIDGET_RENDER[key];
          return (
            <div key={key}>
              <WidgetSlot
                widgetKey={key}
                title={WIDGET_TITLE[key]}
                onHide={handleHide}
                onKeyboardNudge={handleKeyboardNudge}
                layoutEntry={layoutEntry}
              >
                {renderBody()}
              </WidgetSlot>
            </div>
          );
        })}
      </ResponsiveGridLayout>
    </Box>
  );
}
